const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1';

class PortalApiClient {
  private token: string | null = null;

  constructor() {
    // Try to load token from localStorage
    this.token = localStorage.getItem('portalToken');
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('portalToken', token);
  }

  getToken(): string | null {
    return this.token;
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('portalToken');
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || `Request failed with status ${response.status}`);
    }

    return response.json();
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // Portal API Methods

  async verifyToken(token: string): Promise<VerifyResponse> {
    return this.get<VerifyResponse>(`/portal/verify?token=${token}`);
  }

  async getServiceHistory(params?: { limit?: number; offset?: number; startDate?: string; endDate?: string }): Promise<ServiceHistoryResponse> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.set('limit', String(params.limit));
    if (params?.offset) queryParams.set('offset', String(params.offset));
    if (params?.startDate) queryParams.set('startDate', params.startDate);
    if (params?.endDate) queryParams.set('endDate', params.endDate);
    const query = queryParams.toString();
    return this.get<ServiceHistoryResponse>(`/portal/service-history${query ? `?${query}` : ''}`);
  }

  async getServiceDetail(id: string): Promise<ServiceDetail> {
    return this.get<ServiceDetail>(`/portal/service-history/${id}`);
  }

  async getChemicalReadings(params?: { limit?: number; bodyOfWaterId?: string; startDate?: string; endDate?: string }): Promise<ChemicalReadingsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.set('limit', String(params.limit));
    if (params?.bodyOfWaterId) queryParams.set('bodyOfWaterId', params.bodyOfWaterId);
    if (params?.startDate) queryParams.set('startDate', params.startDate);
    if (params?.endDate) queryParams.set('endDate', params.endDate);
    const query = queryParams.toString();
    return this.get<ChemicalReadingsResponse>(`/portal/chemical-readings${query ? `?${query}` : ''}`);
  }

  async getInvoices(params?: { limit?: number; offset?: number }): Promise<InvoicesResponse> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.set('limit', String(params.limit));
    if (params?.offset) queryParams.set('offset', String(params.offset));
    const query = queryParams.toString();
    return this.get<InvoicesResponse>(`/portal/invoices${query ? `?${query}` : ''}`);
  }

  async getInvoiceDetail(id: string): Promise<InvoiceDetail> {
    return this.get<InvoiceDetail>(`/portal/invoices/${id}`);
  }

  async createPaymentSession(
    invoiceId: string,
    paymentMethod?: 'card' | 'us_bank_account',
  ): Promise<{ checkoutUrl: string }> {
    const portalBaseUrl = window.location.origin + '/portal';
    return this.post<{ checkoutUrl: string }>(`/portal/invoices/${invoiceId}/pay`, {
      portalBaseUrl,
      paymentMethod,
    });
  }

  async getWorkOrderDetail(id: string): Promise<WorkOrderItem> {
    return this.get<WorkOrderItem>(`/portal/work-orders/${encodeURIComponent(id)}`);
  }

  async getWorkOrders(params?: { limit?: number; offset?: number; view?: 'upcoming' | 'completed' }): Promise<WorkOrdersResponse> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.set('limit', String(params.limit));
    if (params?.offset) queryParams.set('offset', String(params.offset));
    if (params?.view) queryParams.set('view', params.view);
    const query = queryParams.toString();
    return this.get<WorkOrdersResponse>(`/portal/work-orders${query ? `?${query}` : ''}`);
  }

  async getContactInfo(): Promise<ContactInfo> {
    return this.get<ContactInfo>('/portal/contact');
  }

  async getServiceAgreement(): Promise<ServiceAgreementData> {
    return this.get<ServiceAgreementData>('/portal/service-agreement');
  }

  async getPublicWebsite(slug: string): Promise<PublicWebsiteData> {
    return this.get<PublicWebsiteData>(`/website/public/${encodeURIComponent(slug)}`);
  }

  async getBookingAvailability(slug: string): Promise<BookingAvailability> {
    return this.get<BookingAvailability>(`/website/public/${encodeURIComponent(slug)}/booking/availability`);
  }

  async getBookingAddressSuggestions(slug: string, input: string): Promise<BookingAddressSuggestion[]> {
    const response = await this.get<{ suggestions: BookingAddressSuggestion[] }>(
      `/website/public/${encodeURIComponent(slug)}/booking/address-suggestions?input=${encodeURIComponent(input)}`,
    );
    return response.suggestions;
  }

  async getBookingAddressDetails(slug: string, placeId: string): Promise<BookingAddressDetails | null> {
    const response = await this.get<{ details: BookingAddressDetails | null }>(
      `/website/public/${encodeURIComponent(slug)}/booking/address-details?placeId=${encodeURIComponent(placeId)}`,
    );
    return response.details;
  }

  async createPublicBooking(slug: string, data: CreatePublicBookingRequest): Promise<{ success: boolean; message: string }> {
    return this.post<{ success: boolean; message: string }>(`/website/public/${encodeURIComponent(slug)}/booking`, data);
  }

  async createPublicLead(slug: string, data: CreatePublicLeadRequest): Promise<{ success: boolean; message: string }> {
    return this.post<{ success: boolean; message: string }>(`/website/public/${encodeURIComponent(slug)}/lead`, data);
  }
}

// Types

export interface ServiceAgreementData {
  customerName?: string;
  businessName?: string;
  agreementBody?: string;
  status: string | null;
  sentAt?: string;
  signedAt?: string;
  signatureUrl?: string | null;
}

export interface VerifyResponse {
  valid: boolean;
  error?: string;
  customer?: {
    id: string;
    name: string;
    email: string | null;
    agreementStatus?: string | null;
    properties: {
      id: string;
      name: string | null;
      address: string;
      bodiesOfWater: {
        id: string;
        name: string;
        type: string;
      }[];
    }[];
  };
  account?: {
    id: string;
    name: string;
    slug: string;
    businessName: string;
    businessPhone: string | null;
    businessEmail: string | null;
    businessWebsite: string | null;
    logoUrl: string | null;
    primaryColor: string;
    secondaryColor: string;
    countryCode: string;
    locale: string;
    customerLanguage?: string;
  };
  tokenExpiresAt?: string;
}

export interface PublicWebsiteData {
  account: {
    businessName: string | null;
    businessPhone: string | null;
    businessEmail: string | null;
    slug: string;
    locale?: string;
    customerLanguage?: string;
  };
  settings: {
    logoUrl?: string | null;
    primaryColor?: string | null;
    secondaryColor?: string | null;
    bookingEnabled?: boolean;
    bookingHeadline?: string | null;
    bookingDescription?: string | null;
    bookingServiceTypes?: string[];
    bookingShowPrice?: boolean;
    bookingShowDuration?: boolean;
    contactFormEnabled?: boolean;
    contactFormHeadline?: string | null;
    contactFormFields?: string[];
  };
}

export interface BookingServiceOption {
  id: string;
  name: string;
  description: string | null;
  price: number | null;
  priceType: string | null;
  duration: number | null;
}

export interface BookingAvailableDay {
  date: string;
  dayName: string;
  routeId: string;
  routeName: string;
}

export interface BookingAvailability {
  showPrice: boolean;
  showDuration: boolean;
  currencyCode?: string;
  locale?: string;
  customerLanguage?: string;
  businessName?: string | null;
  logoUrl?: string | null;
  primaryColor?: string | null;
  bookingHeadline?: string | null;
  bookingDescription?: string | null;
  bookingServiceTypes?: string[];
  services: BookingServiceOption[];
  availableDays: BookingAvailableDay[];
}

export interface BookingAddressSuggestion {
  placeId: string;
  mainText: string;
  secondaryText: string;
  fullText: string;
}

export interface BookingAddressDetails {
  formattedAddress: string;
  addressLine1: string;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
  latitude: number | null;
  longitude: number | null;
}

export interface CreatePublicBookingRequest {
  routeId: string;
  date: string;
  serviceType: string;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  customerAddress?: string;
  notes?: string;
}

export interface CreatePublicLeadRequest {
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  serviceInterest?: string;
  referralSource?: string;
  message?: string;
  sourcePage?: string;
}

export interface ServiceHistoryItem {
  logType?: string;
  reportTitle?: string | null;
  id: string;
  serviceDate: string;
  arrivedAt: string | null;
  completedAt: string | null;
  durationMinutes: number | null;
  status: string;
  technicianName: string | null;
  property: {
    id: string;
    name: string | null;
    address: string;
  };
  bodyOfWater: {
    id: string;
    name: string;
    type: string;
  } | null;
  techNotes: string | null;
  checklistItems: any;
  photoCount: number;
  hasChemicalReading: boolean;
}

export interface ServiceHistoryResponse {
  items: ServiceHistoryItem[];
  total: number;
  limit: number;
  offset: number;
  showTechnicianAndTiming: boolean;
}

export interface PortalInspectionReport {
  reportTitle: string;
  welcomeLetter?: string;
  disclaimer?: string;
  coverPhotoUrl?: string;
  sections: { slug: string; label: string; items: {
    id: string; label: string; condition: string | null; notes: string; photoUrls: string[];
  }[] }[];
}

export interface ServiceDetail {
  logType?: string;
  inspectionData?: PortalInspectionReport | null;
  reportDocument?: { id: string; fileName: string; url: string } | null;
  id: string;
  serviceDate: string;
  arrivedAt: string | null;
  completedAt: string | null;
  durationMinutes: number | null;
  status: string;
  skipReason: string | null;
  showIdealChemicalRanges: boolean;
  showTechnicianAndTiming: boolean;
  technicianName: string | null;
  property: {
    reportLabel?: string | null;
    id: string;
    name: string | null;
    address: string;
  };
  bodyOfWater: {
    id: string;
    name: string;
    type: string;
  } | null;
  techNotes: string | null;
  checklistItems: any;
  photos: {
    id: string;
    url: string;
    thumbnailUrl: string | null;
    caption: string | null;
    photoType: string;
  }[];
  chemicalReading: {
    chlorine: number | null;
    ph: number | null;
    alkalinity: number | null;
    calciumHardness: number | null;
    cyanuricAcid: number | null;
    salt: number | null;
    waterTemp: number | null;
    phosphates: number | null;
    psi: number | null;
    chemicalsAdded: any;
  } | null;
}

export interface ChemicalReading {
  id: string;
  date: string;
  bodyOfWater: {
    id: string;
    name: string;
    type: string;
  };
  chlorine: number | null;
  ph: number | null;
  alkalinity: number | null;
  calciumHardness: number | null;
  cyanuricAcid: number | null;
  salt: number | null;
  waterTemp: number | null;
  phosphates: number | null;
  psi: number | null;
}

export interface ChemicalReadingsResponse {
  bodiesOfWater: {
    id: string;
    name: string;
    type: string;
  }[];
  readings: ChemicalReading[];
}

export interface InvoiceItem {
  id: string;
  invoiceNumber: string;
  status: string;
  issueDate: string;
  dueDate: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  subtotal: number;
  taxAmount: number;
  taxBreakdown?: TaxBreakdown[];
  total: number;
  amountPaid: number;
  amountDue: number;
  currencyCode?: string;
  locale?: string;
  pdfUrl: string | null;
  stripeInvoiceUrl: string | null;
  lineItemCount: number;
  paymentCount: number;
}

export interface InvoicesResponse {
  items: InvoiceItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface InvoiceDetail {
  id: string;
  invoiceNumber: string;
  status: string;
  issueDate: string;
  dueDate: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  subtotal: number;
  taxAmount: number;
  taxBreakdown?: TaxBreakdown[];
  total: number;
  amountPaid: number;
  amountDue: number;
  currencyCode?: string;
  locale?: string;
  notes: string | null;
  footerNotes: string | null;
  pdfUrl: string | null;
  stripeInvoiceUrl: string | null;
  lineItems: {
    id: string;
    description: string;
    quantity: number;
    unitPrice: number;
  amount: number;
  propertyId?: string | null;
    serviceDate: string | null;
  }[];
  payments: {
    id: string;
    amount: number;
    method: string;
    status: string;
    processedAt: string;
  }[];
  paymentInstructions: any;
  achEnabled: boolean;
  processingFeeEnabled: boolean;
  cardTotalWithFee?: number;
  achTotalWithFee?: number;
}

export interface TaxBreakdown {
  pricesIncludeTax?: boolean;
  taxRateId?: string | null;
  name: string;
  rate: number;
  taxableAmount: number;
  taxAmount: number;
}

export interface WorkOrderItem {
  logType?: string | null;
  id: string;
  type: string;
  status: string;
  scheduledDate: string | null;
  completedAt: string | null;
  estimatedDuration: number;
  description: string | null;
  notes: string | null;
  property: {
    id: string;
    name: string | null;
    address: string;
  } | null;
  technicianName: string | null;
  serviceLogId: string | null;
}

export interface WorkOrdersResponse {
  items: WorkOrderItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface ContactInfo {
  businessName: string;
  phone: string | null;
  email: string | null;
  website: string | null;
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  businessHours: {
    dayOfWeek: number;
    isOpen: boolean;
    openTime: string | null;
    closeTime: string | null;
  }[];
  socialLinks: {
    facebook: string | null;
    instagram: string | null;
    yelp: string | null;
    google: string | null;
    nextdoor: string | null;
  };
}

export const portalApi = new PortalApiClient();
